/**
 * Personel hiyerarşi yardımcıları (dinamik rol/yetki sistemi sonrası).
 *
 * `parent_personnel_id` zincirini yukarı doğru takip eder; her seviyede
 * `Personnel` kaydı + rol bilgisi içeren bir node döner. PersonnelDetailsDialog
 * ve PersonnelForm "Özet" sekmesi tarafından kullanılır.
 *
 * Sprint 1-3 geçiş döneminde `parent_personnel_id` boş gelirse eski
 * `formen_id`/`bolge_muhendisi_id`/`mudur_yardimcisi_id` zinciri kullanılır
 * (fallback). Sprint 4'te eski FK'ler drop edilince fallback kaldırılır.
 *
 * NOT: Sistem admini (slug='admin') zincirden hariç tutulur — admin teknik
 * bir rol, personel hiyerarşisinin parçası değil.
 */

import { isSystemAdminPersonnel } from "./roleUtils";

const MAX_CHAIN_DEPTH = 10;

/**
 * Bir personelin direkt amirini (parent_personnel_id veya eski FK fallback)
 * döner. allPersonnel'de bulunamazsa null.
 */
function findDirectParent(p, allById) {
  if (!p) return null;
  if (p.parent_personnel_id) {
    const direct = allById.get(String(p.parent_personnel_id));
    if (direct) return direct;
  }
  // Geçiş dönemi fallback: eski FK'lerden hiyerarşi seviyesine göre en yakın amir
  // (park_gorevlisi/cim_ekibi → formen, formen → bolge_muhendisi, ...)
  const role = String(p.role || "");
  if (role === "park_gorevlisi") {
    if (p.formen_id) return allById.get(String(p.formen_id)) || null;
  }
  if (role === "formen") {
    if (p.bolge_muhendisi_id) return allById.get(String(p.bolge_muhendisi_id)) || null;
  }
  if (role === "bolge_muhendisi") {
    if (p.mudur_yardimcisi_id) return allById.get(String(p.mudur_yardimcisi_id)) || null;
  }
  return null;
}

/**
 * `personnel`'in amir zincirini (kendisi HARİÇ, yukarı doğru) hesaplar.
 *
 * @param {object} personnel - başlangıç personeli
 * @param {Array} allPersonnel - tüm personel listesi (lookup için)
 * @param {{ getRoleById?: Function, getRoleBySlug?: Function }} roleHelpers - useRoles hook'undan
 * @returns {Array<{ id, name, role_slug, role_display_name, hierarchy_level, assignment_type }>}
 */
export function computeParentChain(personnel, allPersonnel, roleHelpers = {}) {
  if (!personnel || !Array.isArray(allPersonnel)) return [];
  const allById = new Map();
  for (const p of allPersonnel) {
    if (p?.id) allById.set(String(p.id), p);
  }
  const { getRoleById, getRoleBySlug } = roleHelpers;
  const chain = [];
  const seen = new Set([String(personnel.id)]);
  let current = findDirectParent(personnel, allById);
  let depth = 0;
  while (current && depth < MAX_CHAIN_DEPTH) {
    const idStr = String(current.id);
    if (seen.has(idStr)) break; // cycle guard
    seen.add(idStr);
    // Sistem admini hiyerarşi dışıdır — zincirde gösterme, ama yukarı doğru
    // takibi durdurma (admin bir mudur'un parent'ı ise zaten olmamalı, fallback).
    if (isSystemAdminPersonnel(current, { getRoleById })) {
      current = findDirectParent(current, allById);
      depth += 1;
      continue;
    }
    const roleDef =
      (getRoleById && current.role_id ? getRoleById(current.role_id) : null) ||
      (getRoleBySlug && current.role ? getRoleBySlug(current.role) : null);
    chain.push({
      id: current.id,
      name: current.name,
      role_slug: current.role || roleDef?.slug || "",
      role_display_name: roleDef?.display_name || current.role || "",
      hierarchy_level: Number(roleDef?.hierarchy_level ?? 0),
      assignment_type: current.assignment_type || "NORMAL",
    });
    current = findDirectParent(current, allById);
    depth += 1;
  }
  return chain;
}

export default computeParentChain;
